import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    Organization,
    OrgPart,
    OrgHat,
    PolicyTargets,
    PolicyPartTarget,
    PolicyUserTarget,
    PolicyHatTarget,
    PolicyTargetMode,
} from '@newgenus/common';
import { MaterialModule } from '../../../material.module';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';

/**
 * Input data for the target selector dialog
 */
export interface PolicyTargetSelectorDialogInput {
    organization: Organization;
    currentTargets?: PolicyTargets;
    mode: 'view' | 'edit';
}

/**
 * Tree node for displaying org structure
 */
export interface OrgTreeNode {
    key: string;
    name: string;
    partType: string;
    level: number;
    children: OrgTreeNode[];
    // Selection state
    isSelected: boolean;
    includeChildren: boolean;
    isIncludedViaParent: boolean;
    parentKey?: string;
    // Stats
    userCount: number;
    hatCount: number;
}

/**
 * Result returned from the dialog
 */
export interface PolicyTargetSelectorDialogResult {
    targets: PolicyTargets;
    summary: {
        partCount: number;
        hatCount: number;
        userCount: number;
    };
}

@Component({
    selector: 'shared-org-policy-target-selector',
    imports: [MaterialModule, CommonModule, FormsModule, MatTreeModule],
    templateUrl: './org-policy-target-selector.html',
    styleUrls: ['./org-policy-target-selector.scss', '../org-dialog.scss'],
})
export class OrgPolicyTargetSelectorComponent implements OnInit {
    // === Tree Data ===
    public treeControl = new NestedTreeControl<OrgTreeNode>((node) => node.children);
    public dataSource = new MatTreeNestedDataSource<OrgTreeNode>();
    public orgTreeData: OrgTreeNode[] = [];

    // === Selection State ===
    public selectedParts: Map<string, PolicyPartTarget> = new Map();
    public selectedHats: Map<string, PolicyHatTarget> = new Map();
    public selectedUsers: Map<string, PolicyUserTarget> = new Map();
    public targetMode: PolicyTargetMode = 'selected';

    // === Quick Actions ===
    public quickSelectAll = false;

    // === Search ===
    public searchTerm = '';
    public filteredTreeData: OrgTreeNode[] = [];

    // === Summary Stats ===
    public summary = {
        partCount: 0,
        hatCount: 0,
        userCount: 0,
    };

    // === Part Type Icons ===
    public partTypeIcons: Record<string, string> = {
        executive: 'business',
        division: 'account_tree',
        department: 'folder',
        section: 'folder_open',
        unit: 'workspaces',
        post: 'person',
    };

    constructor(
        public dialogRef: MatDialogRef<OrgPolicyTargetSelectorComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PolicyTargetSelectorDialogInput
    ) {}

    ngOnInit(): void {
        this.buildOrgTree();
        this.loadCurrentTargets();
        this.updateSummary();
    }

    // === Tree Building ===

    /**
     * Build the organization tree from orgParts
     * OrgParts is a flat array - we need to reconstruct the hierarchy using parentPartKey
     */
    private buildOrgTree(): void {
        const org = this.data.organization;
        const orgParts = org?.orgParts || [];

        // First, create a map of all nodes
        const nodeMap = new Map<string, OrgTreeNode>();
        const rootNodes: OrgTreeNode[] = [];

        // Create all nodes first
        for (const part of orgParts) {
            const partKey = part.partKey || part.key || '';
            const node: OrgTreeNode = {
                key: partKey,
                name: part.name || part.mainTitle || partKey,
                partType: part.partType || this.inferPartType(part.depth || 0),
                level: part.depth || 0,
                children: [],
                isSelected: false,
                includeChildren: false,
                isIncludedViaParent: false,
                parentKey: part.parentPartKey,
                userCount: this.countUsersInPart(partKey),
                hatCount: this.countHatsInPart(partKey),
            };
            nodeMap.set(partKey, node);
        }

        // Now build the hierarchy
        for (const part of orgParts) {
            const partKey = part.partKey || part.key || '';
            const node = nodeMap.get(partKey);
            if (!node) continue;

            const parent = part.parentPartKey ? nodeMap.get(part.parentPartKey) : null;
            if (parent) {
                // Add as child of parent
                parent.children.push(node);
            } else {
                // Root node (no parent or parent not found)
                rootNodes.push(node);
            }
        }

        // Sort children by id/name for consistent ordering
        this.sortChildrenRecursively(rootNodes);

        this.orgTreeData = rootNodes;
        this.dataSource.data = this.orgTreeData;
        this.filteredTreeData = this.orgTreeData;

        // Expand first two levels by default
        this.expandToLevel(this.orgTreeData, 2);
    }

    /**
     * Sort children recursively for consistent display
     */
    private sortChildrenRecursively(nodes: OrgTreeNode[]): void {
        nodes.sort((a, b) => a.name.localeCompare(b.name));
        for (const node of nodes) {
            if (node.children.length > 0) {
                this.sortChildrenRecursively(node.children);
            }
        }
    }

    /**
     * Expand tree to specified level
     */
    private expandToLevel(nodes: OrgTreeNode[], maxLevel: number, currentLevel = 0): void {
        if (currentLevel >= maxLevel) return;

        for (const node of nodes) {
            this.treeControl.expand(node);
            if (node.children.length > 0) {
                this.expandToLevel(node.children, maxLevel, currentLevel + 1);
            }
        }
    }

    /**
     * Recursively convert OrgPart array to tree nodes (legacy - for pre-nested data)
     */
    private convertPartsToTree(parts: OrgPart[], level: number, parentKey?: string): OrgTreeNode[] {
        return parts.map((part) => {
            const partKey = part.partKey || part.key || '';
            const children = part.children
                ? this.convertPartsToTree(part.children as OrgPart[], level + 1, partKey)
                : [];

            return {
                key: partKey,
                name: part.name || part.mainTitle || partKey,
                partType: part.partType || this.inferPartType(level),
                level,
                children,
                isSelected: false,
                includeChildren: false,
                isIncludedViaParent: false,
                parentKey,
                userCount: this.countUsersInPart(partKey),
                hatCount: this.countHatsInPart(partKey),
            };
        });
    }

    /**
     * Infer part type from level if not specified
     */
    private inferPartType(level: number): string {
        const types = ['executive', 'division', 'department', 'section', 'unit', 'post'];
        return types[Math.min(level, types.length - 1)];
    }

    /**
     * Count users assigned to parts/hats under this part
     */
    private countUsersInPart(partKey: string): number {
        const org = this.data.organization;
        const orgHats = org?.orgHats;
        let count = 0;

        if (orgHats?.hatsKeys) {
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey] as OrgHat;
                if (hat && hat.positionArray?.includes(partKey)) {
                    count += hat.assignedToArray?.length || hat.userKeys?.length || 0;
                }
            }
        }

        return count;
    }

    /**
     * Count hats associated with this part
     */
    private countHatsInPart(partKey: string): number {
        const org = this.data.organization;
        const orgHats = org?.orgHats;
        let count = 0;

        if (orgHats?.hatsKeys) {
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey] as OrgHat;
                if (hat && hat.positionArray?.includes(partKey)) {
                    count++;
                }
            }
        }

        return count;
    }

    // === Load Existing Targets ===

    private loadCurrentTargets(): void {
        const current = this.data.currentTargets;
        if (!current) return;

        this.targetMode = current.mode || 'selected';

        // Load selected parts
        if (current.parts) {
            for (const part of current.parts) {
                this.selectedParts.set(part.key, part);
                // Find and mark the node
                this.markNodeSelected(this.orgTreeData, part.key, part.includeChildren);
            }
        }

        // Load selected hats
        if (current.directHats) {
            for (const hat of current.directHats) {
                this.selectedHats.set(hat.key, hat);
            }
        }

        // Load selected users
        if (current.directUsers) {
            for (const user of current.directUsers) {
                this.selectedUsers.set(user.uid, user);
            }
        }
    }

    /**
     * Mark a node as selected in the tree
     */
    private markNodeSelected(nodes: OrgTreeNode[], key: string, includeChildren: boolean): boolean {
        for (const node of nodes) {
            if (node.key === key) {
                node.isSelected = true;
                node.includeChildren = includeChildren;
                if (includeChildren) {
                    this.markChildrenIncluded(node.children);
                }
                return true;
            }
            if (node.children.length > 0) {
                if (this.markNodeSelected(node.children, key, includeChildren)) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Mark all children as included via parent
     */
    private markChildrenIncluded(nodes: OrgTreeNode[]): void {
        for (const node of nodes) {
            node.isIncludedViaParent = true;
            if (node.children.length > 0) {
                this.markChildrenIncluded(node.children);
            }
        }
    }

    // === Selection Actions ===

    /**
     * Toggle selection of a node
     */
    public toggleNodeSelection(node: OrgTreeNode): void {
        if (node.isIncludedViaParent) {
            // Can't directly select if included via parent
            return;
        }

        node.isSelected = !node.isSelected;

        if (node.isSelected) {
            // Add to selected parts
            this.selectedParts.set(node.key, {
                key: node.key,
                name: node.name,
                partType: node.partType,
                includeChildren: node.includeChildren,
            });
        } else {
            // Remove from selected parts
            this.selectedParts.delete(node.key);
            node.includeChildren = false;
            // Unmark children
            this.unmarkChildrenIncluded(node.children);
        }

        this.updateSummary();
    }

    /**
     * Toggle cascade (include children as a bundle)
     */
    public toggleIncludeChildren(node: OrgTreeNode, event: Event): void {
        event.stopPropagation();

        if (!node.isSelected) {
            // Auto-select if not already selected
            node.isSelected = true;
            this.selectedParts.set(node.key, {
                key: node.key,
                name: node.name,
                partType: node.partType,
                includeChildren: true,
            });
        }

        node.includeChildren = !node.includeChildren;

        // Update the selected part
        const selected = this.selectedParts.get(node.key);
        if (selected) {
            selected.includeChildren = node.includeChildren;
        }

        // Update children state
        if (node.includeChildren) {
            // When bundling, remove any individually selected children
            this.removeChildrenFromSelection(node.children);
            this.markChildrenIncluded(node.children);
        } else {
            this.unmarkChildrenIncluded(node.children);
        }

        this.updateSummary();
    }

    /**
     * Select all children recursively as individual selections
     * This allows each child to be independently managed
     */
    public selectAllChildrenRecursive(node: OrgTreeNode, event: Event): void {
        event.stopPropagation();

        // Make sure we expand the node so user sees what was selected
        this.treeControl.expand(node);

        // Recursively select all children
        this.selectChildrenRecursively(node.children);

        this.updateSummary();
    }

    /**
     * Recursively select all children nodes
     */
    private selectChildrenRecursively(nodes: OrgTreeNode[]): void {
        for (const child of nodes) {
            // Skip if already included via parent bundle
            if (child.isIncludedViaParent) continue;

            // Select this child
            if (!child.isSelected) {
                child.isSelected = true;
                this.selectedParts.set(child.key, {
                    key: child.key,
                    name: child.name,
                    partType: child.partType,
                    includeChildren: false,
                });
            }

            // Expand to show selected children
            if (child.children.length > 0) {
                this.treeControl.expand(child);
                this.selectChildrenRecursively(child.children);
            }
        }
    }

    /**
     * Remove children from individual selection (when parent bundles them)
     */
    private removeChildrenFromSelection(nodes: OrgTreeNode[]): void {
        for (const node of nodes) {
            if (node.isSelected) {
                node.isSelected = false;
                node.includeChildren = false;
                this.selectedParts.delete(node.key);
            }
            if (node.children.length > 0) {
                this.removeChildrenFromSelection(node.children);
            }
        }
    }

    /**
     * Check if all children are already selected
     */
    public areAllChildrenSelected(node: OrgTreeNode): boolean {
        return this.checkAllChildrenSelected(node.children);
    }

    private checkAllChildrenSelected(nodes: OrgTreeNode[]): boolean {
        if (nodes.length === 0) return true;

        for (const child of nodes) {
            if (!child.isSelected && !child.isIncludedViaParent) {
                return false;
            }
            if (child.children.length > 0 && !this.checkAllChildrenSelected(child.children)) {
                return false;
            }
        }
        return true;
    }

    /**
     * Count all descendants of a node
     */
    public countAllDescendants(node: OrgTreeNode): number {
        let count = 0;
        for (const child of node.children) {
            count++;
            count += this.countAllDescendants(child);
        }
        return count;
    }

    /**
     * Unmark children as included via parent
     */
    private unmarkChildrenIncluded(nodes: OrgTreeNode[]): void {
        for (const node of nodes) {
            node.isIncludedViaParent = false;
            if (node.children.length > 0) {
                this.unmarkChildrenIncluded(node.children);
            }
        }
    }

    // === Quick Actions ===

    /**
     * Select all organization
     */
    public selectAllOrganization(): void {
        this.targetMode = 'all_organization';
        this.clearAllSelections();
        this.updateSummary();
    }

    /**
     * Clear to selected mode
     */
    public selectTargetedMode(): void {
        this.targetMode = 'selected';
        this.updateSummary();
    }

    /**
     * Select all of a specific part type
     */
    public selectAllOfType(partType: string): void {
        this.targetMode = 'selected';
        this.selectNodesByType(this.orgTreeData, partType);
        this.updateSummary();
    }

    private selectNodesByType(nodes: OrgTreeNode[], partType: string): void {
        for (const node of nodes) {
            if (node.partType === partType && !node.isIncludedViaParent) {
                node.isSelected = true;
                this.selectedParts.set(node.key, {
                    key: node.key,
                    name: node.name,
                    partType: node.partType,
                    includeChildren: false,
                });
            }
            if (node.children.length > 0) {
                this.selectNodesByType(node.children, partType);
            }
        }
    }

    /**
     * Clear all selections
     */
    public clearAllSelections(): void {
        this.selectedParts.clear();
        this.selectedHats.clear();
        this.selectedUsers.clear();
        this.clearNodeSelections(this.orgTreeData);
        this.updateSummary();
    }

    private clearNodeSelections(nodes: OrgTreeNode[]): void {
        for (const node of nodes) {
            node.isSelected = false;
            node.includeChildren = false;
            node.isIncludedViaParent = false;
            if (node.children.length > 0) {
                this.clearNodeSelections(node.children);
            }
        }
    }

    // === Search ===

    public applySearch(): void {
        if (!this.searchTerm.trim()) {
            // Reset to full tree
            this.dataSource.data = this.orgTreeData;
            this.expandToLevel(this.orgTreeData, 2);
            return;
        }

        const term = this.searchTerm.toLowerCase();
        const filtered = this.filterTree(this.orgTreeData, term);
        this.dataSource.data = filtered;

        // Expand all matching nodes so results are visible
        this.expandAllNodes(filtered);
    }

    private filterTree(nodes: OrgTreeNode[], term: string): OrgTreeNode[] {
        return nodes
            .map((node) => {
                const matchesSearch =
                    node.name.toLowerCase().includes(term) || node.partType.toLowerCase().includes(term);
                const filteredChildren = this.filterTree(node.children, term);

                if (matchesSearch || filteredChildren.length > 0) {
                    // Create a new node with filtered children
                    return {
                        ...node,
                        children: matchesSearch ? node.children : filteredChildren,
                    };
                }
                return null;
            })
            .filter((node): node is OrgTreeNode => node !== null);
    }

    /**
     * Expand all nodes in the tree (used after search)
     */
    private expandAllNodes(nodes: OrgTreeNode[]): void {
        for (const node of nodes) {
            this.treeControl.expand(node);
            if (node.children.length > 0) {
                this.expandAllNodes(node.children);
            }
        }
    }

    public clearSearch(): void {
        this.searchTerm = '';
        this.dataSource.data = this.orgTreeData;
        this.expandToLevel(this.orgTreeData, 2);
    }

    // === Summary Calculation ===

    private updateSummary(): void {
        if (this.targetMode === 'all_organization') {
            // Count all users in org
            this.summary = this.countAllOrgUsers();
        } else {
            // Count selected
            this.summary = this.countSelectedTargets();
        }
    }

    private countAllOrgUsers(): { partCount: number; hatCount: number; userCount: number } {
        const org = this.data.organization;
        const orgParts = org?.orgParts || [];
        const orgHats = org?.orgHats;

        const partCount = this.countAllParts(orgParts);
        const hatCount = orgHats?.hatsKeys?.length || 0;

        // Count unique users
        const userSet = new Set<string>();
        if (orgHats?.hatsKeys) {
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey] as OrgHat;
                if (hat) {
                    const users = hat.assignedToArray || hat.userKeys || [];
                    users.forEach((uid) => userSet.add(uid));
                }
            }
        }

        return {
            partCount,
            hatCount,
            userCount: userSet.size,
        };
    }

    private countAllParts(parts: OrgPart[]): number {
        let count = parts.length;
        for (const part of parts) {
            if (part.children) {
                count += this.countAllParts(part.children as OrgPart[]);
            }
        }
        return count;
    }

    private countSelectedTargets(): { partCount: number; hatCount: number; userCount: number } {
        let partCount = 0;
        let hatCount = 0;
        const userSet = new Set<string>();

        // Count from selected parts
        for (const [key, part] of this.selectedParts) {
            partCount++;
            const node = this.findNode(this.orgTreeData, key);
            if (node) {
                if (part.includeChildren) {
                    const childStats = this.countChildrenStats(node.children);
                    partCount += childStats.partCount;
                    hatCount += childStats.hatCount;
                    childStats.users.forEach((uid) => userSet.add(uid));
                }
                hatCount += node.hatCount;
                this.getUsersInPart(key).forEach((uid) => userSet.add(uid));
            }
        }

        // Add directly selected hats
        hatCount += this.selectedHats.size;

        // Add directly selected users
        this.selectedUsers.forEach((_, uid) => userSet.add(uid));

        return {
            partCount,
            hatCount,
            userCount: userSet.size,
        };
    }

    private findNode(nodes: OrgTreeNode[], key: string): OrgTreeNode | null {
        for (const node of nodes) {
            if (node.key === key) return node;
            if (node.children.length > 0) {
                const found = this.findNode(node.children, key);
                if (found) return found;
            }
        }
        return null;
    }

    private countChildrenStats(nodes: OrgTreeNode[]): { partCount: number; hatCount: number; users: Set<string> } {
        let partCount = 0;
        let hatCount = 0;
        const users = new Set<string>();

        for (const node of nodes) {
            partCount++;
            hatCount += node.hatCount;
            this.getUsersInPart(node.key).forEach((uid) => users.add(uid));

            if (node.children.length > 0) {
                const childStats = this.countChildrenStats(node.children);
                partCount += childStats.partCount;
                hatCount += childStats.hatCount;
                childStats.users.forEach((uid) => users.add(uid));
            }
        }

        return { partCount, hatCount, users };
    }

    private getUsersInPart(partKey: string): string[] {
        const org = this.data.organization;
        const orgHats = org?.orgHats;
        const users: string[] = [];

        if (orgHats?.hatsKeys) {
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey] as OrgHat;
                if (hat && hat.positionArray?.includes(partKey)) {
                    const hatUsers = hat.assignedToArray || hat.userKeys || [];
                    users.push(...hatUsers);
                }
            }
        }

        return users;
    }

    // === Tree Helpers ===

    public hasChildren = (_: number, node: OrgTreeNode): boolean => {
        return node.children && node.children.length > 0;
    };

    public getPartIcon(partType: string): string {
        return this.partTypeIcons[partType] || 'folder';
    }

    // === Dialog Actions ===

    public confirm(): void {
        const result: PolicyTargetSelectorDialogResult = {
            targets: {
                mode: this.targetMode,
                parts: Array.from(this.selectedParts.values()),
                directHats: Array.from(this.selectedHats.values()),
                directUsers: Array.from(this.selectedUsers.values()),
            },
            summary: this.summary,
        };

        this.dialogRef.close(result);
    }

    public cancel(): void {
        this.dialogRef.close(null);
    }
}
